import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import { GameState } from '../systems/GameState';
import { SaveSystem } from '../systems/SaveSystem';
import { AudioManager } from '../systems/AudioManager';
import { OUTFITS, HAIR_FULL_KEY } from '../data/outfits';
import {
  PET_SHEET_KEY, PET_SHEET_PATH, PET_SHEET_FRAME,
  PET_SHEET_SPECIES, PET_SHEET_ACTIONS, PetRenderer,
} from '../systems/PetRenderer';

export class BootScene extends Phaser.Scene {
  constructor() { super('BootScene'); }

  preload(): void {
    this.load.image('__placeholder', 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkAAIAAAoAAv/lxKUAAAAASUVORK5CYII=');
    // BGM 트랙 (CC0 — OpenGameArt). 씬마다 3곡씩 랜덤 재생.
    const tracks: [string, string][] = [
      ['home',          'audio/home.mp3'],
      ['home_alt1',     'audio/home_alt1.mp3'],
      ['home_alt2',     'audio/home_alt2.mp3'],
      ['closet',        'audio/closet.mp3'],
      ['closet_alt1',   'audio/closet_alt1.mp3'],
      ['closet_alt2',   'audio/closet_alt2.mp3'],
      ['minigame',      'audio/minigame.mp3'],
      ['minigame_alt1', 'audio/minigame_alt1.mp3'],
      ['minigame_alt2', 'audio/minigame_alt2.mp3'],
      ['runway',        'audio/runway.mp3'],
      ['runway_alt1',   'audio/runway_alt1.mp3'],
      ['runway_alt2',   'audio/runway_alt2.mp3'],
      ['petroom',       'audio/petroom.mp3'],
      ['petroom_alt1',  'audio/petroom_alt1.mp3'],
      ['petroom_alt2',  'audio/petroom_alt2.mp3'],
      ['shop',          'audio/shop.mp3'],
      ['shop_alt1',     'audio/shop_alt1.mp3'],
      ['shop_alt2',     'audio/shop_alt2.mp3'],
      ['math',          'audio/math.mp3'],
      ['math_alt1',     'audio/math_alt1.mp3'],
      ['math_alt2',     'audio/math_alt2.mp3'],
    ];
    for (const [key, path] of tracks) {
      this.load.audio(key, path);
    }
    // AI 생성 캐릭터 베이스 (도트 디테일 업그레이드)
    this.load.image('character_base', 'images/character/base.png');
    this.load.image('character_base_small', 'images/character/base_small.png');
    this.load.image('character_bald', 'images/character/bald.png');

    // 헤어 풀캐릭터 PNG — 6 시트 × 48 = 288개, AI 그리드 생성으로 정렬 보장
    // 각 PNG = 빡빡이 + 헤어가 통합된 192×288 캐릭터
    for (const o of OUTFITS) {
      if (o.slot === 'hair') {
        const fname = o.id.replace(/^hair_/, '');
        this.load.image(HAIR_FULL_KEY(o.id), `images/character/full/${fname}.png`);
      }
    }

    // AI 생성 펫 스프라이트 — species 단위로 키 = `pet_<species>`
    const PET_SPECIES = [
      'dog', 'cat', 'rabbit', 'hamster', 'dragon', 'turtle', 'chick', 'pig',
      'panda', 'bear', 'dolphin', 'unicorn',
      // 2차 생성 — 가용 시 자동 사용
      'bird', 'penguin', 'fox', 'owl', 'deer', 'sheep', 'monkey', 'frog',
      'butterfly', 'ladybug', 'elephant', 'lion',
      'tiger', 'koala', 'raccoon', 'squirrel', 'shark', 'hedgehog', 'wolf',
      'whale', 'ferret', 'alpaca', 'capybara', 'crab',
    ];
    for (const sp of PET_SPECIES) {
      this.load.image(`pet_${sp}`, `images/pets/${sp}.png`);
    }

    // 펫 애니메이션 시트 (dog/cat/rabbit/hamster × idle/walk/sit/lie/cry/cheer)
    this.load.spritesheet(PET_SHEET_KEY, PET_SHEET_PATH, {
      frameWidth: PET_SHEET_FRAME,
      frameHeight: PET_SHEET_FRAME,
    });

    // OGA 파티클 / UI 자산
    const PARTICLES = ['sparkle', 'sparkle_soft', 'heart_symbol', 'star_burst', 'star_small', 'magic_ring', 'magic_circle', 'explosion_poof', 'flame', 'flare', 'glow_circle'];
    for (const p of PARTICLES) {
      this.load.image(`particle_${p}`, `images/particles/${p}.png`);
    }

    // 씬 배경 일러스트
    const BGS: [string, string][] = [
      ['bg_home',     'images/bg/home.png'],
      ['bg_closet',   'images/bg/closet.jpg'],
      ['bg_petroom',  'images/bg/petroom.png'],
      ['bg_shop',     'images/bg/shop.png'],
      ['bg_minigame', 'images/bg/minigame.png'],
      ['bg_runway',   'images/bg/runway.png'],
      ['bg_math',     'images/bg/math.png'],
    ];
    for (const [k, p] of BGS) {
      this.load.image(k, p);
    }

    // SFX (CC0) — 키 = AudioManager.playSfx 호출 키와 일치. 캐시에 있으면 자동 사용.
    const SFX_KEYS = ['click', 'equip', 'purchase', 'star', 'correct', 'wrong', 'levelup', 'pet_happy', 'beat_hit', 'win', 'lose', 'toast'];
    for (const k of SFX_KEYS) {
      this.load.audio(k, `audio/sfx/${k}.mp3`);
    }

    // 누락 자산은 무시 — fallback 시스템으로 작동 계속
    this.load.on('loaderror', (file: { key: string; type: string }) => {
      console.warn(`[Boot] asset missing: ${file.key} (fallback active)`);
    });
  }

  async create(): Promise<void> {
    // 펫 시트 애니메이션 등록 (전 씬 공유)
    if (this.textures.exists(PET_SHEET_KEY)) {
      for (const sp of PET_SHEET_SPECIES) {
        for (const act of PET_SHEET_ACTIONS) {
          const key = PetRenderer.getAnimKey(sp, act);
          if (this.anims.exists(key)) continue;
          const idleFrame = PetRenderer.getSheetFrame(sp, 'idle');
          const actFrame = PetRenderer.getSheetFrame(sp, act);
          // walk/cheer: 두 프레임 교대로 보행/들썩 효과
          if (act === 'walk' || act === 'cheer') {
            this.anims.create({
              key,
              frames: [
                { key: PET_SHEET_KEY, frame: idleFrame },
                { key: PET_SHEET_KEY, frame: actFrame },
              ],
              frameRate: act === 'walk' ? 5 : 4,
              repeat: -1,
            });
          } else {
            // idle/sit/lie/cry: 단일 프레임 정적
            this.anims.create({
              key,
              frames: [{ key: PET_SHEET_KEY, frame: actFrame }],
              frameRate: 1,
              repeat: 0,
            });
          }
        }
      }
    }

    // Splash screen
    this.cameras.main.setBackgroundColor('#fef0f5');

    // Decorative circles
    for (let i = 0; i < 8; i++) {
      const x = Math.random() * GAME_WIDTH;
      const y = Math.random() * GAME_HEIGHT;
      const r = 20 + Math.random() * 60;
      this.add.circle(x, y, r, 0xffd6e6, 0.18);
    }

    // Logo text
    const logo = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 60, "Roha's Closet", {
      fontFamily: 'Pretendard Variable, sans-serif',
      fontSize: '54px',
      fontStyle: '900',
      color: '#ff6fa1',
      stroke: '#ffffff',
      strokeThickness: 4,
    }).setOrigin(0.5).setAlpha(0);

    const sub = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 30, '로하의 패션 드레스업 게임 ✨', {
      fontFamily: 'Pretendard Variable, sans-serif',
      fontSize: '22px',
      fontStyle: '700',
      color: '#9b6bff',
    }).setOrigin(0.5).setAlpha(0);

    const loadingDots = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 80, '로딩 중...', {
      fontFamily: 'Pretendard Variable, sans-serif',
      fontSize: '16px',
      color: '#b899cc',
    }).setOrigin(0.5).setAlpha(0);

    // Fade in logo
    this.tweens.add({ targets: logo, alpha: 1, duration: 500, ease: 'Cubic.Out' });
    this.tweens.add({ targets: sub, alpha: 1, duration: 500, delay: 200, ease: 'Cubic.Out' });
    this.tweens.add({ targets: loadingDots, alpha: 1, duration: 400, delay: 400, ease: 'Cubic.Out' });

    // Subtle pulse on logo
    this.tweens.add({
      targets: logo,
      scaleX: 1.02,
      scaleY: 1.02,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
      delay: 500,
    });

    await document.fonts.ready;

    // Init AudioContext on first user gesture — register on window so it
    // survives scene transitions. Phaser scene-level input listeners die
    // when the scene stops (which happens immediately below).
    const initAudioOnce = (): void => {
      AudioManager.init();
      window.removeEventListener('pointerdown', initAudioOnce, true);
      window.removeEventListener('keydown', initAudioOnce, true);
      window.removeEventListener('touchstart', initAudioOnce, true);
    };
    window.addEventListener('pointerdown', initAudioOnce, true);
    window.addEventListener('keydown', initAudioOnce, true);
    window.addEventListener('touchstart', initAudioOnce, true);

    // Hold splash for at least 1.2s so user sees the logo
    await new Promise<void>(resolve => this.time.delayedCall(1200, resolve));

    // Fade out then continue
    this.cameras.main.fadeOut(400);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      const existing = SaveSystem.load();
      if (existing) {
        GameState.initialize(existing.profile.name);
        this.scene.start('HomeScene');
      } else {
        this.scene.start('CharacterCreateScene');
      }
    });
  }
}
